import { SapiService, SapiFacetResponse } from './../../../services/sapi.service';
import {
	Component, Input, ViewChild, ElementRef, SimpleChanges, Renderer2, Output, EventEmitter, ChangeDetectionStrategy,
	ChangeDetectorRef, Optional, Self, HostBinding, forwardRef, AfterViewInit, OnChanges, OnDestroy, OnInit
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { FilterSet } from '../../../services/filter-set.service';
import { EntityStoreService } from '../../..//services/entity-store.service';
import { Subscription, Subject, fromEvent } from 'rxjs';
import { QueryParameter } from '../../../interfaces/queryString';
import { distinctUntilChanged, debounceTime, map } from 'rxjs/operators';
import { MatAutocompleteTrigger, MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { _countGroupLabelsBeforeOption, _getOptionScrollPosition } from '@angular/material/core';
import { ContextMenuOption } from '../bb-context-menu/bb-context-menu.component';
import { Router } from '@angular/router';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { ModalDefinition, OverlayService } from '../../../services/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
	selector: 'bb-entity-selector',
	templateUrl: './bb-entity-selector.component.html',
	styleUrls: ['./bb-entity-selector.component.scss'],
	providers: [{
		provide: MatFormFieldControl,
		useExisting: forwardRef(() => BbEntitySelectorComponent)
	}],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class BbEntitySelectorComponent implements MatFormFieldControl<any>, ControlValueAccessor, OnInit, AfterViewInit, OnDestroy, OnChanges {
	// for MatFormFieldControl
	static nextId = 0;
	@HostBinding() id = `bb-entity-selector-${BbEntitySelectorComponent.nextId++}`;
	@HostBinding('class.floating') get shouldLabelFloat() {
		return this.focused || !this.empty;
	}
	@Input('aria-describedby') userAriaDescribedBy: string;
	@Input() get required() {
		return this._required;
	}
	set required(req) {
		this._required = coerceBooleanProperty(req);
		this.stateChanges.next();
	}

	@Input() filterSet: FilterSet = null;
	@Input() options: any[] = [];
	@Input() entityType: string;
	@Input() facet: string;
	@Input() multi = false;
	@Input() hideIcons = false;
	@Input() hideValue = false;
	@Input() extendable = false;
	@Input() disableClear = false;
	@Input() selectOnly = false;
	@Input() startOpen = false;
	@Input() hideThumbnail = false;
	@Input() isSubmitted = false;
	@Input() partials: any[] = [];
	@Input() partialText = 'Add to all';
	@Input() theme: 'default' | 'dark' | 'analytics' | 'light-v2' = 'default';
	@Input() valueProp: string;
	@Input() labelProp: string;
	@Input() icon: string;
	@Input() placeholder: string;
	@Input() excludedOptions: string[] = [];
	@Input() extraOptions: any[] = []; // only implemented for use with entityType
	@Input() inMatFormField = false;

	@Output() optionSelected: EventEmitter<any> = new EventEmitter<any>();
	@Output() optionRemoved: EventEmitter<any> = new EventEmitter<any>();
	@Output() closed: EventEmitter<void> = new EventEmitter<void>();

	@ViewChild('inputAutoComplete') inputRef: ElementRef;
	@ViewChild(MatAutocompleteTrigger) private inputAutoComplete: MatAutocompleteTrigger;
	@ViewChild(MatAutocomplete) private auto: MatAutocomplete;


	@Input() get disabled(): boolean {
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = coerceBooleanProperty(value);
		this.stateChanges.next();
	}

	// TODO: implement or remove inlineSearch
	inlineSearch: boolean;
	// @Input() inlineSearch: boolean = false;

	searchResults: any[];
	formControl: UntypedFormControl;
	inputValue = '';
	filteredOptions: any[] = [];
	numFound = 0;
	filterSetQuery: FilterSet = [];
	searched = false;
	searching = false;
	isOpen = false;
	selectedOptions: any = [];
	queryValue = '';
	docMouseUpListener: any;
	optionHeight: any;
	contextMenuStyle: any = { display: 'none' };
	contextMenuOptions: ContextMenuOption[] = [];
	entityMapping: any;
	isDestroyed = false;
	placeholderText: string;
	searchValue = '';
	autocompleteClass: string;
	panelPosition = 'auto';

	// for MatFormFieldControl
	controlType = 'bb-entity-selector';
	touched = false;
	stateChanges = new Subject<void>();
	classNameIdPrefixRef = { // add more when needed
		auction: 'Auction_'
	};
	private _disabled = false;
	private _required = false;

	private subscriptions = new Subscription();
	private _modelValue: any;

	constructor(
		private sapiService: SapiService,
		private entityStoreService: EntityStoreService,
		private router: Router,
		private overlayService: OverlayService,
		private translate: TranslateService,
		private changeDetector: ChangeDetectorRef,
		private renderer: Renderer2,
		@Optional() @Self() public ngControl: NgControl,
		protected hostElement: ElementRef
	) {
		this.formControl = new UntypedFormControl();
		// Replace the provider from above with this.
		if (this.ngControl != null) {
			// Setting the value accessor directly (instead of using
			// the providers) to avoid running into a circular import.
			this.ngControl.valueAccessor = this;
		}
	}

	ngOnInit() {
		this.stateChanges.pipe(debounceTime(1))
			.subscribe(() => {
				if (!this.isDestroyed) {
					this.changeDetector.detectChanges();
				}
			});

		if (this.icon === undefined) {
			this.icon = this.theme === 'light-v2' ? 'caret-down' : 'chevron-down';
		}
		this.autocompleteClass = 'theme-' + this.theme + ' bb-entity-selector-autocomplete';
		this.setProps();

		if (this.placeholder === undefined && !this.inMatFormField) {
			if (this.extendable) {
				this.placeholderText = this.translate.instant('SELECT_OR_ADD') + '...';
			} else {
				this.placeholderText = this.translate.instant('SELECT') + '...';
			}
		}

		if (this.selectOnly) {
			this.modelValue = [];
		}

		if (this.startOpen) {
			setTimeout(() => {
				this.open();
			});
		}
	}

	setProps() {
		if (this.entityType && (!this.options || !this.options.length)) {
			this.entityMapping = this.entityStoreService.getEntityMapping(this.entityType);
			this.labelProp = this.labelProp || this.entityMapping.label || 'label';
			this.valueProp = this.valueProp || this.entityMapping.value || (this.entityType ? 'id' : 'value');
		} else {
			this.labelProp = 'label';
			this.valueProp = 'value';
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.options && !changes.options.firstChange && changes.options.currentValue && changes.options.currentValue.length) {
			this.filteredOptions = this.filterOutSelected(changes.options.currentValue);
			this.stateChanges.next();
		} else if (changes.facet && !changes.facet.firstChange && changes.facet.currentValue) {
			this.query();
		}
		if (changes.filterSet) {
			this.searched = false;
			this.filteredOptions = [];
		}
		if (changes.labelProp || changes.valueProp || changes.entityType || changes.options) {
			if (changes.entityType && !changes.entityType.firstChange) {
				this.labelProp = null;
				this.valueProp = null;
			}
			this.setProps();
		}
		if (changes.extendable && changes.extendable.currentValue) {
			if (!this.placeholder && !this.inMatFormField) {
				if (this.extendable) {
					this.placeholderText = this.translate.instant('SELECT_OR_ADD') + '...';
				} else {
					this.placeholderText = this.translate.instant('SELECT') + '...';
				}
			}
		}
		if (changes.placeholder && changes.placeholder.currentValue) {
			this.placeholderText = this.placeholder;
			// for MatFormFieldControl
			this.stateChanges.next();
		}
		if (changes.excludedOptions && !changes.excludedOptions.firstChange) {
			this.query();
		}
	}

	ngOnDestroy() {
		this.isDestroyed = true;
		if (this.docMouseUpListener) {
			this.docMouseUpListener();
		}
		this.subscriptions.unsubscribe();

		// for MatFormFieldControl
		this.stateChanges.complete();
	}

	ngAfterViewInit() {
		this.updateOptions();

		// because we have changeDetection: ChangeDetectionStrategy.OnPush update the view when entityStoreService gets new values
		this.subscriptions.add(this.sapiService.getSapiChanges().subscribe((sapiChange: any) => {
			this.stateChanges.next();
		}));

		if (this.hostElement && this.hostElement.nativeElement) {
			Object.defineProperty(this.hostElement.nativeElement, 'value', {
				get: () => this._modelValue,
				set: (val: any) => {
					this.modelValue = val;
					this.propagateChange(this.modelValue);
					this.stateChanges.next();
				}
			});
		}

		this.subscriptions.add(fromEvent(this.inputRef.nativeElement, 'input').pipe(
			map((v: any) => {
				if (this.entityType || this.facet) {
					this.searching = true;
				}
				return v;
			}),
			debounceTime((this.entityType || this.facet) ? 500 : 1),
			distinctUntilChanged(),
		).subscribe((ev: Event) => {
			this.onInputChange(this.inputRef.nativeElement.value);
		}));
	}

	displayFn(value: any) {
		if (!value) {
			return '';
		}
		const index = this.filteredOptions.findIndex(option => option && (option[this.valueProp] || option.id) === value);
		if (index !== -1) {
			// this.selectedOptions[0] = this.filteredOptions[index];
		}
		return this.getLabel(this.selectedOptions);
	}

	log(val: any) {
		console.warn('log', val);
	}

	aliasVisible(alias: string) {
		if (!this.queryValue) {
			return false;
		}
		const q = this.queryValue.toLowerCase();
		return alias.toLowerCase().indexOf(q) !== -1;
	}

	determinePanelPosition() {
		const hostRect = this.hostElement?.nativeElement?.getBoundingClientRect();
		const bodyRect = document.body.getBoundingClientRect();
		this.panelPosition = bodyRect?.height - hostRect?.y < 310 ? 'above' : 'auto';
	}

	onInputChange(query: string) {
		this.queryValue = query;
		if (this.entityType) {
			this.query(query);
		} else if (this.options && this.options.length) {
			if (!query) {
				this.filteredOptions = this.filterOutSelected(this.options);
			} else {
				this.filteredOptions = this.filterOutSelected(this.options.filter((option: any) => {
					const q = query.toLowerCase();
					const labelIncludes = option.label && option.label.toLowerCase && option.label.toLowerCase().indexOf(q) !== -1;
					let aliasIncludes = false;
					// let matchingAliasIndex = -1;
					if (option.aliases && option.aliases.length) {
						option.aliases.forEach((alias: string, i: number) => {
							if (!aliasIncludes && this.aliasVisible(alias)) {
								aliasIncludes = true;
								// matchingAliasIndex = i;
							}
						});
					}
					return labelIncludes || aliasIncludes;
				}));
				this.filteredOptions.sort((option: any) => option.label && option.label.toLowerCase().indexOf(query.toLowerCase()) === 0 ? -1 : 1);
			}
		}
		this.stateChanges.next();
	}

	onPaste(ev: any) {
		if (this.extendable && this.multi) {
			ev.preventDefault();

			// get text without HTML
			let pasted = (ev.originalEvent || ev).clipboardData.getData('text/plain') || '';
			pasted = pasted.trim(); // in order to remove leading/trailing newlines

			let tags: string[] = [];
			if (pasted.indexOf('\n') >= 0) {
				tags = pasted.split('\n');
			} else {
				tags = pasted.split(',');
			}

			const newModelValue: string[] = (this.modelValue || []).concat(tags);
			this.modelValue = newModelValue;
			this.propagateChange(this.modelValue);
			this.close();
		}
	}

	onValueClicked(ev: MouseEvent, isPartial: boolean, option: any) {
		if (isPartial && option && ev) {
			ev.stopPropagation();
			this.optionSelected.emit(option);
		}
	}

	onDisplayValueClick(ev: MouseEvent) {
		this.determinePanelPosition();
		if (!this.searched && this.entityType && !this.searching) {
			this.query();
		}
		ev.stopPropagation();
		this.open();
		if (this.extendable && !this.multi && this._modelValue) {
			this.searchValue = this._modelValue;
		} else {
			this.searchValue = '';
		}
		this.stateChanges.next();
	}

	onInputFocus(ev: any) {
		ev.target.select();
		if (!this.searched && this.entityType && !this.searching) {
			this.query();
		}
	}

	getTypePrefixFromEntityType(entityType) {
		return this.classNameIdPrefixRef[entityType] || (entityType.charAt(0).toUpperCase() + entityType.slice(1) + '_');
	}

	query(term?: string, id?: string) {
		const offset = 0;
		const amount = 15;
		let query = '*:*';
		let sortTerm = 'id';
		const r = new RegExp(/\S*\d+\S*/g);
		if (this.facet) {
			query = '*:*';
		} else if (term) {
			term = term.trim();
			const parts = term.split(' ');
			if (parts.length === 1) {
				const tokenizedSearch = term.match(r)
					? ` OR "${term}"`
					: ` OR (originalfilenameSort:${term} OR originalfilenameSort:${term}*)^100 OR (${term} OR ${term}*)^1`;
				query = `(title_cistr:*${term}*)^100 OR (name:${term} OR name:${term}*)^100 OR (code:${term
				} OR code:${term}*)^100 OR (label: ${term} OR label:${term}*)^100${tokenizedSearch}`;
			} else if (parts.length === 2) {
				/* eslint-disable-next-line max-len */
				query = `("${term}")^10000 OR ((title:"${parts.join('" AND title:"')}") OR (title:"${parts.join('" AND title:*')}*))^5000 OR ((${parts.map(part => term.match(r) ? `"${part}"` : part).join(' AND ')}) OR (${parts.map(part => term.match(r) ? `"${part}"` : part).join(' AND ')}*))^500`;
			} else {
				query = `("${parts.join('" AND "')}") OR (${parts.join(' AND ')}*)`;
			}
			sortTerm = 'score';
		} else if (term === '') {
			query = '*:*';
		} else if (id) {
			query = 'id:' + id;
		}

		// exclude already selected values
		let excludeArr = [];
		if (this.modelValue) {
			excludeArr = typeof this.modelValue === 'object' && this.modelValue.length ? this.modelValue : [this.modelValue];
		}
		if (this.excludedOptions && this.excludedOptions.length) {
			excludeArr = excludeArr.concat(this.excludedOptions);
		}
		if (excludeArr && typeof excludeArr === 'object' && excludeArr.length) {
			if (this.valueProp === 'id') {
				excludeArr = excludeArr.map(eid => this.getTypePrefixFromEntityType(this.entityType) + eid);
			}
			query = query !== '*:*' ? `(${query})` : query;
			query += ` AND -${this.valueProp}:"${excludeArr.join(`" AND -${this.valueProp}:"`)}"`;
		}

		const queryParams: QueryParameter[] = [];
		queryParams.push({ name: 'q', values: [query] } as QueryParameter);

		this.searching = true;

		if (this.facet) {
			this.sapiService.getFacetOptions(this.entityType, this.facet, 15, term || '', this.filterSet).subscribe((response: SapiFacetResponse) => {
				this.searched = true;
				this.searching = false;
				this.filteredOptions = [];
				if (response && response.facetList) {
					this.searchResults = response.facetList.map((facetkey) => ({
						label: facetkey,
						value: facetkey
					}));
					this.filteredOptions = this.filterOutSelected(this.searchResults);
				}
				this.stateChanges.next();
			});
		} else {
			this.sapiService.query(this.entityType, queryParams, offset, amount, sortTerm, 'desc', this.filterSet).subscribe(response => {
				this.searched = true;
				this.searching = false;
				this.filteredOptions = [];
				if (response && response.items && response.items.length > 0) {
					this.numFound = response.totalAmount;
					for (let i = 0; i < response.items.length; i++) {
						if (response.items[i]) {
							this.filteredOptions.push(response.items[i]);
						}
					}
				}
				if (this.extraOptions?.length) {
					const filteredExtraOptions = this.extraOptions.filter((option) => !term || option.originalfilename?.toLowerCase().includes(term?.toLowerCase()));
					this.filteredOptions = this.filteredOptions.concat(filteredExtraOptions);
				}
				this.stateChanges.next();
			});
		}
	}

	labelInOptions(v: string) {
		return !!(this.filteredOptions
			&& this.filteredOptions.find((option) => option.label && v && option.label.toLowerCase && option.label.toLowerCase() === v.toLowerCase()));
	}

	labelInSelectedOptions(v: string) {
		return !!(this.selectedOptions.length
			&& this.selectedOptions.find((option: any) => option.label && v && option.label.toLowerCase && option.label.toLowerCase() === v.toLowerCase()));
	}

	optionInSelectedOptions(option: any) {
		return this.selectedOptions.find((o: any) => o.value === option[this.valueProp]);
	}

	filterOutSelected(options: any[]) {
		// console.warn('options pre', options, 'this.selectedOptions', this.selectedOptions);
		const ret = options.filter((option) => !this.optionInSelectedOptions(option));
		return ret;
	}

	onMatOptionSelected(option: MatAutocompleteSelectedEvent) {
		if (this.multi) {
			const mv = this.modelValue || [];
			mv.push(option.option.value);
			this.modelValue = mv;
			this.propagateChange(this.modelValue);
			this.close();
		} else {
			this.modelValue = option.option.value;
			this.propagateChange(option.option.value);
			this.close();
		}
		if (option) {
			this.inputRef.nativeElement.value = '';
			this.onInputChange(this.inputRef.nativeElement.value);
		}

		let selectedOption = this.selectedOptions.find((o: any) => o && (o[this.valueProp] || o.id || o.value) === option.option.value);
		if (!selectedOption && this.extraOptions?.length) {
			selectedOption = this.extraOptions.find((o: any) => o && (o[this.valueProp] || o.id || o.value) === option.option.value);
		}

		this.updateOptions();
		this.optionSelected.emit(selectedOption);

		if (this.selectOnly) {
			this.modelValue = [];
		}
	}

	onMatAutocompleteOpened() {
		this.open();
	}

	onDocumentMouseUp(ev: MouseEvent) {
		const t = (ev.target as HTMLElement);
		if (!t ||
			(
				!t.classList.contains('mat-autocomplete-panel') &&
				!t.classList.contains('value-text')
			)
		) {
			this.close();
		}
		this.stateChanges.next();
	}

	onMatAutocompleteClosed() {
		this.close();
	}

	open() {
		this.isOpen = true;
		if (!this.docMouseUpListener) {
			this.docMouseUpListener = this.renderer.listen(document, 'mouseup', (ev: MouseEvent) => {
				this.onDocumentMouseUp(ev);
			});
		}
		this.inputAutoComplete.openPanel();
		setTimeout(() => {
			this.inputRef.nativeElement.focus();
			this.stateChanges.next();
		});
	}

	close() {
		this.isOpen = false;
		if (this.docMouseUpListener) {
			this.docMouseUpListener();
			this.docMouseUpListener = null;
		}
		this.updateOptions();
		setTimeout(() => {
			this.inputAutoComplete.closePanel();
			this.stateChanges.next();
		});
		this.closed.emit();
	}

	getLabel(option?: any) {
		if (this.options && option && (option[this.labelProp] || option.value)) {
			return option[this.labelProp] || option.value;
		}

		// let entity: SapiEntity = (option && option.type) ? option as SapiEntity : {type: this.entityType};
		const entityRef: any = option || this.entityStoreService.getEntity(this.entityType, this.inputRef.nativeElement.value);
		if (entityRef) {
			return entityRef[this.labelProp] || entityRef.originalfilename || entityRef[this.valueProp] || this.inputRef.nativeElement.value || '';
		}

		return ''; // this.inputRef.nativeElement.value || ''
	}

	clearSelectedOption(ev: MouseEvent) {
		ev.stopPropagation();
		this.modelValue = null;
		this.propagateChange(this.modelValue);
		this.updateOptions();
	}

	removeOptionClicked(option: any, index: number, ev: MouseEvent) {
		ev.stopPropagation();
		const mv = this.modelValue;
		mv.splice(index, 1);
		this.modelValue = mv;
		this.propagateChange(this.modelValue);
		this.updateOptions();
		this.optionRemoved.emit(option);
		this.searched = false;
		this.filteredOptions = [];
	}

	getIdLabel(option: any) {
		if (!option || !option.type) {
			return '';
		}
		switch (option.type.toLowerCase()) {
			case 'playout':
				return option.label;
				break;

			default:
				return option[this.valueProp] || option.id || option.code || option.label;
				break;
		}
	}

	updateOptions() {
		if (this.options && this.options.length) {
			this.filteredOptions = this.filterOutSelected(this.options);
		} else if (this.searchResults && this.searchResults.length) {
			this.filteredOptions = this.filterOutSelected(this.searchResults);
		}
		this.stateChanges.next();
	}

	get modelValue(): string[] {
		return this.multi ? this._modelValue : (Array.isArray(this._modelValue) && this._modelValue.length ? this._modelValue[0] : this._modelValue);
	}
	set modelValue(value: string[]) {
		if (typeof value !== 'object' || (value && !value.length)) {
			if (value && value.length !== 0) {
				(value as any) = [value];
			} else {
				value = null;
			}
		}
		this.selectedOptions = [];
		let modelValue = (value ? value : []);
		if (value === null) {
			modelValue = null;
		} else {
			if (this.entityType && !this.facet) {
				let idString = '';
				modelValue.forEach((id: string, i: number) => {
					this.selectedOptions[i] = this.entityStoreService.getEntity(this.entityType, id);
					if (!this.selectedOptions[i]) {
						const extraOptionValue = this.extraOptions?.find((o: any) => o && (o[this.valueProp] || o.id || o.value) === id);
						if (extraOptionValue) {
							this.selectedOptions[i] = extraOptionValue;
						} else {
							idString += `${this.valueProp}:${id}`;
							if (value.length - 1 !== i) {
								idString += ' OR ';
							}
						}
					}
				});
				if (idString) {
					const queryParams: QueryParameter[] = [];
					if (idString) {
						queryParams.push({ name: 'q', values: [idString] } as QueryParameter);
					}
					// TODO fix in backend: filterSet overrules query!
					this.sapiService.query(this.entityType, queryParams, 0, 50, null, null, this.filterSet).subscribe((result) => {
						modelValue.forEach((valuePropValue: string, i: number) => {
							this.selectedOptions[i] = this.entityStoreService.getEntity(this.entityType, valuePropValue);
							if (!this.selectedOptions[i]) {
								this.selectedOptions[i] = {
									id: modelValue[i],
									modelLabel: modelValue[i],
									modelSuffix: `(${this.entityType} not found)`,
									modelType: this.entityType
								};
								this.selectedOptions[i][this.valueProp] = modelValue[i];
							}
						});
						this.stateChanges.next();
					});
				}
			} else if (this.options && this.options.length) {
				modelValue.forEach((v: string, i: number) => {
					this.selectedOptions[i] = this.options.find((option) => option.value === v);
					if (!this.selectedOptions[i]) {
						this.selectedOptions[i] = {
							label: v,
							value: v
						};
					}
				});
			} else {
				modelValue.forEach((v: string, i: number) => {
					this.selectedOptions[i] = {
						label: v,
						value: v
					};
				});
			}
		}
		const selectedOptions = this.selectedOptions.map((o: any) => o && (o[this.valueProp] || o.id || o.value));
		this._modelValue = selectedOptions.length && selectedOptions[0] ? selectedOptions : (value === null ? null : this._modelValue);
		this.stateChanges.next();
	}

	getContextMenuOptions(entity): any {
		const itemNavigationOptions: any = {
			MediaClip: {
				entity,
				quickEditRoute: ['library', 'mediaclip', entity.id, 'metadata'],
				openRoute: ['/library/mediaclip', entity.id],
				preview: () => {
					if (entity?.type === 'MediaClip') {
						this.showClipPreview(entity);
					}
				}
			},
			Auction: {
				entity,
				openRoute: ['/adservices/auction', entity.id],
			},
			AdUnit: {
				entity,
				openRoute: ['/adservices/adunits', entity.id],
			},
			LineItem: {
				entity,
				openRoute: ['/adservices/lineitems', entity.id],
			},
			Playout: {
				entity,
				openRoute: ['/library/playout', entity.id],
			},
			App: { // Template
				entity,
				openRoute: ['/library/template', entity.id],
			},
			MediaClipList: {
				entity,
				quickEditRoute: ['library', 'playlist', entity.id, 'general'],
				openRoute: ['/library/playlist', entity.id],
			}
		};
		return itemNavigationOptions[entity.type];
	}

	contextMenuShow(posX: number, posY: number, menuOptions: any): void {
		if (menuOptions.quickEditRoute) {
			this.contextMenuOptions.push({
				label: 'Quick edit',
				value: 'edit',
				data: {
					entity: menuOptions.entity
				},
				onSelected: (option: any) => {
					if (option.data && option.data.entity) {
						this.router.navigate(['', { outlets: { modal: menuOptions.quickEditRoute } }]);
					}
				},
				icon: 'pencil'
			});
		}
		if (menuOptions.openRoute) {
			this.contextMenuOptions.push({
				label: 'Open',
				value: 'goto',
				data: {
					entity: menuOptions.entity
				},
				onSelected: (option: any) => {
					if (option.data && option.data.entity) {
						this.router.navigate(menuOptions.openRoute);
					}
				},
				icon: 'pencil-square-o'
			});
		}
		if (menuOptions.preview) {
			this.contextMenuOptions.push({
				label: 'preview',
				value: 'preview',
				data: {
					entity: menuOptions.entity
				},
				onSelected: (option: any) => {
					menuOptions.preview();
				},
				icon: 'eye'
			});
		}
		this.contextMenuStyle.display = 'block';
		this.contextMenuStyle.left = posX + 'px';
		this.contextMenuStyle.top = posY + 'px';
		this.stateChanges.next();
	}

	onEntityContextMenu(ev: any, entity: any): void {
		this.contextMenuOptions = [];
		const contextOptions = this.getContextMenuOptions(entity);
		if (ev && entity && contextOptions) {
			const clip = entity as Mediaclip;
			ev.preventDefault();
			ev.stopPropagation();

			this.contextMenuShow(ev.pageX, ev.pageY, contextOptions);
		}
		this.stateChanges.next();
	}

	showClipPreview(clip: Mediaclip) {

		const modal: ModalDefinition = {
			icon: 'film',
			title: (clip.title || clip.originalfilename),
			component: {
				name: 'embed-preview',
				inputs: {
					mediaclip: clip
				}
			}
		};

		this.overlayService.createModal(modal);
	}

	// for MatFormFieldControl
	get value() {
		return this.formControl.value;
	}
	get empty() {
		return this.modelValue?.length === 0 || this.modelValue === null;
	}
	get focused() {
		return this.isOpen;
	}
	get errorState(): boolean {
		return !this.modelValue && this.required && (this.touched || this.isSubmitted);
	}

	setDescribedByIds(ids: string[]) {
		// maybe later, required method.
	}

	onContainerClick(event: MouseEvent) {
		if ((event.target as Element).tagName.toLowerCase() !== 'input') {
			this.open();
			this.hostElement.nativeElement.querySelector('input').focus();
		}
	}

	// ControlValueAccessor fields:
	writeValue(value: string[]) {
		this._modelValue = value;
		this.modelValue = value;

		this.updateOptions();
	}

	propagateChange = (_: any) => { };

	registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	registerOnTouched() {

	}
}
